// eslint-disable-next-line import/named, @typescript-eslint/no-unused-vars
import { ButtonNew, Icon, Input, Typography } from "@mg/dali/src";
import {
  type CreateGuestAccountResponse,
  type VerifyMfaResponse,
  type WorkosLoginResponse,
} from "@mg/schemas/src/prince/auth";
import {
  Button,
  Dialog,
  Flex,
  Grid,
  Text,
  VisuallyHidden,
  TextField,
} from "@radix-ui/themes";
import { useMutation } from "@tanstack/react-query";
// eslint-disable-next-line import/named
import { Link } from "@tanstack/react-router";
import cx from "classnames";
import {
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
  type FormEvent,
} from "react";
import zxcvbn from "zxcvbn";

import { PasswordInput } from "./PasswordInput";
import { PasswordRequirements } from "./PasswordRequirements";

import { ReactComponent as Logo } from "../images/Logo-blue.svg";
import { forgotPasswordRoute } from "../routes/forgot-password/route";
import { MfaForm } from "../routes/login/MfaForm";
import {
  createAccount,
  verifyMfa,
  verifyUserExists,
  workOsLogin,
} from "../services/auth";
import { errorAnalyticsPayload, useAnalytics } from "../utils/analytics";
import { useAppDispatch, useAppSelector } from "../utils/hooks";
import { login } from "../utils/slices/auth";
import { setAuthenticationDialogOpen } from "../utils/slices/ui";

function useVerifyUserMutation() {
  const mutation = useMutation({
    mutationKey: ["verify-user"],
    mutationFn: verifyUserExists,
  });

  return mutation;
}

function useLoginMutation() {
  const mutation = useMutation({
    mutationKey: ["login"],
    mutationFn: workOsLogin,
  });

  return mutation;
}

function useSignupMutation() {
  const mutation = useMutation({
    mutationKey: ["guest-account"],
    mutationFn: createAccount,
  });

  return mutation;
}

function useMfaMutation() {
  const verifyMfaMutation = useMutation({
    mutationKey: ["totp"],
    mutationFn: verifyMfa,
  });

  return verifyMfaMutation;
}

enum AuthStep {
  EMAIL_VERIFICATION = 1,
  LOGIN = 2,
  SIGNUP = 3,
  MFA = 4,
}

export function AuthenticationDialog() {
  const { authenticationDialogOpen } = useAppSelector((state) => state.ui);
  const dispatch = useAppDispatch();
  const posthog = useAnalytics("AuthenticationDialog");
  const user = useAppSelector((state) => state.auth.value);

  const loginMutation = useLoginMutation();
  const signupMutation = useSignupMutation();
  const verifyUserMutation = useVerifyUserMutation();
  const mfaMutation = useMfaMutation();

  const [errorMessage, setErrorMessage] = useState("");
  const [currentStep, setCurrentStep] = useState(AuthStep.EMAIL_VERIFICATION);
  const [email, setEmail] = useState("");
  const [mfaOptions, setMfaOptions] = useState<WorkosLoginResponse>({});
  const [passwordStrength, setPasswordStrength] = useState<{
    password: string;
    score: number;
    suggestions: string[];
  }>({
    password: "",
    score: 0,
    suggestions: [],
  });
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isPasswordVisibilityToggled, togglePasswordVisibility] = useReducer(
    (x) => !x,
    false,
  );
  const [nameValue, setNameValue] = useState("");

  const formRef = useRef<HTMLFormElement>(null);

  const handleVerifyUser = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const startTime = performance.now();
    const formData = new FormData(event.currentTarget);
    const email = formData.get("email") as string;

    verifyUserMutation.mutate(
      {
        email,
      },
      {
        onSuccess: (data) => {
          if (data?.userExists) {
            posthog.capture("authentication_dialog", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              step: "Login",
              referredBy: user?.referredBy,
              email,
            });
            setCurrentStep(AuthStep.LOGIN);
          } else {
            posthog.capture("authentication_dialog", {
              requestDurationSeconds: (performance.now() - startTime) / 1000,
              step: "Signup",
              referredBy: user?.referredBy,
              email,
            });
            setCurrentStep(AuthStep.SIGNUP);
            setNameValue("");
          }
          setEmail(email);
          posthog.capture("authentication_dialog_user_verification", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            email,
            referredBy: user?.referredBy,
          });
        },
        onError: (error) => {
          setErrorMessage(error.message);
          posthog.capture("authentication_dialog_user_verification_err", {
            requestDurationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
            referredBy: user?.referredBy,
            email,
          });
        },
      },
    );
  };

  const handleSuccess = (data: WorkosLoginResponse, startTime: number) => {
    if (data.token) {
      posthog.capture("authentication_dialog_login", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        email,
        referredBy: user?.referredBy,
      });

      dispatch(login({ token: data.token }));
      window.location.reload();
    } else {
      setMfaOptions(data);
      setCurrentStep(AuthStep.MFA);
      posthog.capture("authentication_dialog", {
        requestDurationSeconds: (performance.now() - startTime) / 1000,
        step: "MFA",
        referredBy: user?.referredBy,
        email,
      });
    }
  };

  const handleLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const startTime = performance.now();
    const formData = new FormData(event.target as HTMLFormElement);

    loginMutation.mutate(
      {
        email,
        password: formData.get("password") as string,
      },
      {
        onSuccess: (data) => {
          handleSuccess(data as WorkosLoginResponse, startTime);
        },
        async onError(error) {
          setErrorMessage(error.message);
          posthog.capture("authentication_dialog_login_error", {
            durationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
            email,
            referredBy: user?.referredBy,
          });
        },
      },
    );
  };

  const handleSignUp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const startTime = performance.now();
    const formData = new FormData(event.target as HTMLFormElement);

    signupMutation.mutate(
      {
        email,
        name: nameValue,
        password: formData.get("password") as string,
      },
      {
        onSuccess: (data) => {
          handleSuccess(data as CreateGuestAccountResponse, startTime);
        },
        async onError(error) {
          setErrorMessage(error.message);
          posthog.capture("authentication_dialog_signup_error", {
            durationSeconds: (performance.now() - startTime) / 1000,
            ...errorAnalyticsPayload(error),
            email,
            referredBy: user?.referredBy,
          });
        },
      },
    );
  };

  const checkPasswordLength = useCallback(() => {
    if (formRef.current == null) {
      return;
    }

    const elements = formRef.current.elements;
    // @ts-expect-error TS7015: this is totally valid
    const password = elements["password"] as HTMLInputElement;

    return password?.value.length >= 10;
  }, []);

  const renderForm = () => {
    switch (currentStep) {
      case AuthStep.EMAIL_VERIFICATION:
        return (
          <form onSubmit={handleVerifyUser} className="mt-4 grid gap-4">
            <Grid gap="2">
              <Text weight="medium" htmlFor="email-input">
                Email
              </Text>
              <TextField.Root
                required
                variant="classic"
                name="email"
                id="email-input"
                placeholder="Your email address"
                type="email"
                autoComplete="true"
                size="2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
              <Text
                color="red"
                size="2"
                data-testid="email-error-message"
                className={cx({ hidden: !verifyUserMutation.isError })}
              >
                Request failed with error: {errorMessage}. Please try again.
              </Text>
            </Grid>
            <Flex>
              <Button
                type="submit"
                loading={verifyUserMutation.isPending}
                disabled={verifyUserMutation.isPending}
                className="mt-4 w-full"
              >
                Continue
              </Button>
            </Flex>
          </form>
        );
      case AuthStep.LOGIN:
        return (
          <form onSubmit={handleLogin} className="mt-4 grid gap-4">
            <Grid gap="2">
              <Text weight="medium" htmlFor="password">
                Password
              </Text>
              <PasswordInput
                type="password"
                name="password"
                required
                minLength={10}
                placeholder="••••••••"
                label="Password"
                size="2"
                error={loginMutation.isError}
              />
              <Text
                color="red"
                size="2"
                data-testid="password-error-message"
                className={cx({ hidden: !loginMutation.isError })}
              >
                {errorMessage}
              </Text>
            </Grid>
            <Link to={forgotPasswordRoute.to} className="puntt-link">
              <Text color="blue" weight="medium">
                Forgot Password
              </Text>
            </Link>
            <Button
              className="w-full"
              disabled={loginMutation.isPending}
              loading={loginMutation.isPending}
            >
              Log In
            </Button>
          </form>
        );
      case AuthStep.SIGNUP:
        return (
          <form
            onSubmit={handleSignUp}
            className="mt-4 grid gap-4"
            ref={formRef}
          >
            <Grid gap="2">
              <Text weight="medium" htmlFor="name-input">
                Name
              </Text>
              <TextField.Root
                required
                variant="classic"
                name="name"
                id="name-input"
                placeholder="First Last"
                type="text"
                autoComplete="true"
                size="2"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
              />
            </Grid>

            <Grid gap="2">
              <Text weight="medium" htmlFor="password">
                Password
              </Text>
              <PasswordInput
                label="Password"
                required
                name="password"
                id="password"
                placeholder="••••••••"
                size="2"
                minLength={10}
                disabled={signupMutation.isPending}
                error={
                  passwordTouched &&
                  (signupMutation.isError ||
                    !checkPasswordLength() ||
                    passwordStrength.score < 3)
                }
                variant="classic"
                autoComplete="true"
                onChange={(e) => {
                  if (!passwordTouched) {
                    setPasswordTouched(true);
                  }

                  try {
                    const evaluation = zxcvbn(e.target.value);
                    setPasswordStrength({
                      suggestions: evaluation.feedback.suggestions,
                      score: evaluation.score,
                      password: e.target.value,
                    });
                  } catch (error) {
                    console.error(
                      "Password strength evaluation failed:",
                      error,
                    );
                    // Fallback to basic validation
                    setPasswordStrength({
                      suggestions: [],
                      score: e.target.value.length >= 10 ? 2 : 0,
                      password: e.target.value,
                    });
                  }
                }}
              />
            </Grid>

            <PasswordRequirements
              strength={passwordStrength}
              showInitialRequirements={!passwordTouched}
            />

            {passwordTouched && !checkPasswordLength() && (
              <Text size="2" className="text-puntt-red-10">
                Password must be at least 10 characters long
              </Text>
            )}

            <Text
              color="red"
              size="2"
              data-testid="password-error-message"
              className={cx({ hidden: !signupMutation.isError })}
            >
              {errorMessage}
            </Text>

            <Button
              type="submit"
              disabled={
                signupMutation.isPending ||
                passwordStrength.score < 3 ||
                !checkPasswordLength()
              }
              loading={signupMutation.isPending}
            >
              Create Account
            </Button>
          </form>
        );
      case AuthStep.MFA:
        return (
          <MfaForm
            mfaOptions={mfaOptions}
            loading={mfaMutation.isPending}
            onVerify={(otp) => {
              const startTime = performance.now();
              mfaMutation.mutate(
                {
                  authenticationChallengeId: mfaOptions?.authenticationChallenge
                    ?.id as string,
                  pendingAuthenticationToken:
                    mfaOptions?.pendingAuthenticationToken as string,
                  code: otp,
                },
                {
                  onSuccess: (data) => {
                    dispatch(
                      login({
                        token: (data as VerifyMfaResponse).token as string,
                      }),
                    );
                    posthog.capture("authentication_dialog_mfa_verification", {
                      requestDurationSeconds:
                        (performance.now() - startTime) / 1000,
                      email,
                      referredBy: user?.referredBy,
                    });
                    window.location.reload();
                  },
                  onError: (error) => {
                    setErrorMessage(error.message);
                    posthog.capture(
                      "authentication_dialog_mfa_verification_error",
                      {
                        requestDurationSeconds:
                          (performance.now() - startTime) / 1000,
                        email,
                        ...errorAnalyticsPayload(error),
                        referredBy: user?.referredBy,
                      },
                    );
                  },
                },
              );
            }}
            error={mfaMutation.isError}
            errorMessage={errorMessage}
            guestLogin
          />
        );
      default:
        return null;
    }
  };

  const handleModalClose = () => {
    setEmail("");
    setCurrentStep(AuthStep.EMAIL_VERIFICATION);
    setErrorMessage("");
    setPasswordStrength({
      password: "",
      score: 0,
      suggestions: [],
    });
    setPasswordTouched(false);
    setMfaOptions({});
    loginMutation.reset();
    signupMutation.reset();
    mfaMutation.reset();
    verifyUserMutation.reset();
    if (isPasswordVisibilityToggled) togglePasswordVisibility();
  };

  useEffect(() => {
    if (!authenticationDialogOpen) {
      handleModalClose();
    }
  }, [authenticationDialogOpen]);

  return (
    <Dialog.Root
      open={authenticationDialogOpen}
      onOpenChange={(open) => dispatch(setAuthenticationDialogOpen(open))}
    >
      <Dialog.Content className="w-[400px]" onFocus={(e) => e.preventDefault()}>
        <VisuallyHidden>
          <Dialog.Title>Log in or sign up</Dialog.Title>
        </VisuallyHidden>

        <VisuallyHidden>
          <Dialog.Description>
            To use this application, please log in or create a new account.
          </Dialog.Description>
        </VisuallyHidden>

        {currentStep !== AuthStep.EMAIL_VERIFICATION && (
          <Flex className="mb-4">
            <ButtonNew
              variant="ghost"
              onClick={() => {
                if (currentStep === AuthStep.MFA) {
                  setMfaOptions({});
                  setCurrentStep(AuthStep.LOGIN);
                } else if (currentStep == AuthStep.LOGIN) {
                  setCurrentStep(AuthStep.EMAIL_VERIFICATION);
                  setErrorMessage("");
                  loginMutation.reset();
                } else if (currentStep == AuthStep.SIGNUP) {
                  setCurrentStep(AuthStep.EMAIL_VERIFICATION);
                  setErrorMessage("");
                  setPasswordStrength({
                    password: "",
                    score: 0,
                    suggestions: [],
                  });
                  setPasswordTouched(false);
                  signupMutation.reset();
                }
              }}
            >
              <Icon.ArrowLeft />
              Back
            </ButtonNew>
          </Flex>
        )}
        <Flex align="center" direction="column" gap="4">
          <Logo />
          <Typography as="h3" weight="bold" size="2xl">
            {currentStep === AuthStep.EMAIL_VERIFICATION ||
            currentStep == AuthStep.LOGIN
              ? "Sign in to Puntt"
              : currentStep == AuthStep.SIGNUP
                ? "Sign up for Puntt"
                : "Set up an authenticator app"}
          </Typography>
          {currentStep !== AuthStep.MFA && (
            <Typography as="p" className="text-center">
              {currentStep === AuthStep.EMAIL_VERIFICATION
                ? "Log in or create an account to get access to all available features."
                : currentStep === AuthStep.LOGIN
                  ? "Log in to get access to all available features."
                  : "Create an account to get access to all available features."}
            </Typography>
          )}
        </Flex>

        {renderForm()}
      </Dialog.Content>
    </Dialog.Root>
  );
}

export function useAuthenticationOverlay() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth.value);
  const posthog = useAnalytics("useAuthenticationOverlay");

  useEffect(() => {
    const el = document.getElementById("root");

    if (el == null) return;

    const handleClick = (event: MouseEvent) => {
      if (!user) return;

      const target = event.target as HTMLElement | null;

      if (target == null) return;

      const triggerElem = target.closest("[data-auth-trigger]");

      if (triggerElem && user.referredBy != null) {
        event.preventDefault();
        event.stopImmediatePropagation();
        dispatch(setAuthenticationDialogOpen(true));
        posthog.capture("authentication_dialog_open", {
          referredBy: user?.referredBy,
          trigger: triggerElem.getAttribute("data-auth-trigger"),
        });
      }
    };

    const handleDragOver = (event: DragEvent) => {
      if (!user) return;

      if (user.referredBy != null) {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    const handleDrop = (event: DragEvent) => {
      if (!user) return;

      if (user.referredBy != null) {
        event.preventDefault();
        event.stopPropagation();
        dispatch(setAuthenticationDialogOpen(true));
        posthog.capture("authentication_dialog_open", {
          referredBy: user?.referredBy,
          trigger: "file_drop",
        });
      }
    };

    el.addEventListener("click", handleClick, { capture: true });
    el.addEventListener("dragover", handleDragOver);
    el.addEventListener("drop", handleDrop);

    return () => {
      el.removeEventListener("click", handleClick, { capture: true });
      el.removeEventListener("dragover", handleDragOver);
      el.removeEventListener("drop", handleDrop);
    };
  }, [dispatch, posthog, user]);

  return null;
}
